import type { AdminOfferContainer, AdminSportsbook, OfferRegion, OfferWriteEntry } from '../admin/api'
import { useEffect, useMemo, useState } from 'react'
import {
  createOfferContainer,
  deleteOfferContainer,
  fetchSportOfferContainers,
  renameOfferContainer,
  updateContainerOffers,
} from '../admin/api'
import StateGrid from '../admin/StateGrid'
import StringList from '../admin/StringList'
import { assetUrl } from '../api'
import { STATE_CODE_TO_NAME } from '../embed/stateNames'

/**
 * Offers panel — the working half of the owner-only /affiliate sport page.
 *
 * Persisted since migration 0044: containers + offers + regions live in
 * Supabase (seeded by scripts/import_offers.ts from the WP export). Container
 * create/rename/delete write immediately; offer edits accumulate locally and
 * persist per-container via SAVE (full-replace RPC, one audit row per save).
 *
 * Split layout per Cole (2026-07-07): the LEFT column owns the data —
 * container switcher, the offer list with the edit form inline under the
 * selected row (new offers insert at the top with the form open), and the
 * per-offer region pickers (US state grid + free-form international). The
 * RIGHT column is a pure live preview: renders the cards and supports
 * drag-to-reorder, nothing else.
 */

interface Props {
  sportId: number
  // Catalog books + the ids carried by this sport — the sportsbook picker
  // offers the carriers when the sport has any, else the whole catalog.
  books: AdminSportsbook[]
  carrierIds: number[]
}

type Layout = 'three' | 'two' | 'one' | 'sidebar'

interface EditorOffer {
  id: number // > 0 persisted, negative = created this session
  order: number
  status: string
  sportsbook_id: number | null
  company: string // display label; catalog name when a book is picked
  headline: string
  terms: string
  regulatory_full: string // per-offer override; '' = inherit the book default
  cta_text: string
  cta_url: string
  logo: string // import-fallback image URL; catalog logo wins when book set
  brand_hex: string | null // import-fallback color, same deal
  expires_at: string | null // ISO
  regions: OfferRegion[]
}

interface EditorContainer {
  id: number
  name: string
  offers: EditorOffer[]
}

const EDITABLE: Array<{ key: 'headline' | 'cta_text' | 'cta_url' | 'terms', label: string }> = [
  { key: 'headline', label: 'OFFER NAME' },
  { key: 'cta_text', label: 'BUTTON' },
  { key: 'cta_url', label: 'LINK' },
  { key: 'terms', label: 'TERMS' },
]

let newSeq = 0

function blankOffer(): EditorOffer {
  newSeq += 1
  return {
    id: -newSeq,
    order: 0,
    status: 'draft',
    sportsbook_id: null,
    company: '',
    headline: '',
    terms: '',
    regulatory_full: '',
    cta_text: 'Get Bonus',
    cta_url: '',
    logo: '',
    brand_hex: null,
    expires_at: null,
    regions: [],
  }
}

const US_CODES = new Set(Object.keys(STATE_CODE_TO_NAME))
const HEX_RE = /^#[0-9a-f]{6}$/i

function fromApi(rows: AdminOfferContainer[]): EditorContainer[] {
  return rows.map(c => ({
    id: c.id,
    name: c.name,
    offers: c.offers
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((o, idx) => ({
        id: o.id,
        order: idx + 1,
        status: o.status,
        sportsbook_id: o.sportsbook_id,
        company: o.company_label,
        headline: o.headline,
        terms: o.terms,
        regulatory_full: o.regulatory_full,
        cta_text: o.cta_text,
        cta_url: o.cta_url,
        logo: o.logo_url,
        brand_hex: o.brand_hex,
        expires_at: o.expires_at,
        regions: o.offer_regions ?? [],
      })),
  }))
}

function toPayload(offers: EditorOffer[]): OfferWriteEntry[] {
  return offers.map(o => ({
    id: o.id > 0 ? o.id : null,
    sportsbook_id: o.sportsbook_id,
    company_label: o.company,
    headline: o.headline,
    terms: o.terms,
    regulatory_full: o.regulatory_full,
    cta_text: o.cta_text,
    cta_url: o.cta_url,
    logo_url: o.logo,
    brand_hex: o.brand_hex && HEX_RE.test(o.brand_hex) ? o.brand_hex : null,
    status: o.status === 'published' ? 'published' : 'draft',
    expires_at: o.expires_at,
    regions: o.regions,
  }))
}

export default function OffersPanel({ sportId, books, carrierIds }: Props) {
  const [containerList, setContainerList] = useState<EditorContainer[] | null>(null)
  // Serialized payload per container id at last load/save — dirty detection.
  const [baseline, setBaseline] = useState<Record<number, string>>({})
  const [containerId, setContainerId] = useState<number | null>(null)
  const [layout, setLayout] = useState<Layout>('sidebar')
  const [editingId, setEditingId] = useState<number | null>(null)
  // null = closed; string = draft name. Shared input for new + rename.
  const [newName, setNewName] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [dragId, setDragId] = useState<number | null>(null)
  // Offer ids whose full regulatory statement is expanded in the preview.
  // In-place expand, multiple open at once (2026-07-16 design).
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  function seed(rows: AdminOfferContainer[]) {
    const mapped = fromApi(rows)
    setContainerList(mapped)
    const base: Record<number, string> = {}
    for (const c of mapped) base[c.id] = JSON.stringify(toPayload(c.offers))
    setBaseline(base)
  }

  useEffect(() => {
    setContainerList(null)
    setContainerId(null)
    setEditingId(null)
    setMsg(null)
    fetchSportOfferContainers(sportId)
      .then(seed)
      .catch(e => setMsg({ kind: 'err', text: (e as Error).message }))
  }, [sportId])

  const container = useMemo(() => {
    if (!containerList?.length)
      return null
    return containerList.find(c => c.id === containerId) ?? containerList[0]
  }, [containerList, containerId])

  const availableBooks = useMemo(() => {
    const pool = carrierIds.length ? books.filter(b => carrierIds.includes(b.id)) : books
    return [...pool].sort((a, b) => a.name.localeCompare(b.name))
  }, [books, carrierIds])

  const offers = container?.offers ?? []
  const dirty = container ? JSON.stringify(toPayload(offers)) !== baseline[container.id] : false

  function update(mutate: (list: EditorOffer[]) => EditorOffer[]) {
    if (!container)
      return
    const id = container.id
    setContainerList(prev => (prev ?? []).map(c => (c.id === id ? { ...c, offers: mutate(c.offers) } : c)))
  }

  function setField(id: number, key: keyof EditorOffer, value: string | null) {
    update(list => list.map(o => (o.id === id ? { ...o, [key]: value } : o)))
  }

  // Picking a book stamps the tracking identity from the catalog; brand
  // color + logo resolve from the catalog at render time.
  function setBook(id: number, bookId: number) {
    const book = books.find(b => b.id === bookId)
    if (!book)
      return
    update(list => list.map(o => (o.id === id
      ? { ...o, sportsbook_id: book.id, company: book.name }
      : o)))
  }

  function setUsRegions(id: number, usCodes: string[]) {
    update(list => list.map((o) => {
      if (o.id !== id)
        return o
      const intl = o.regions.filter(r => !US_CODES.has(r.code))
      return {
        ...o,
        regions: [
          ...usCodes.map(code => ({ code, name: STATE_CODE_TO_NAME[code] ?? code })),
          ...intl,
        ],
      }
    }))
  }

  // Free-form international names; no code registry yet, so code = name
  // until the real region model lands.
  function setIntlRegions(id: number, names: string[]) {
    update(list => list.map((o) => {
      if (o.id !== id)
        return o
      const us = o.regions.filter(r => US_CODES.has(r.code))
      return { ...o, regions: [...us, ...names.map(n => ({ code: n, name: n }))] }
    }))
  }

  // Live drag reorder: the dragged offer takes the slot of whichever card it
  // passes over; order renumbers 1..n.
  function reorderTo(targetId: number) {
    if (dragId == null || dragId === targetId)
      return
    update((list) => {
      const from = list.findIndex(o => o.id === dragId)
      const to = list.findIndex(o => o.id === targetId)
      if (from < 0 || to < 0)
        return list
      const next = [...list]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next.map((o, idx) => ({ ...o, order: idx + 1 }))
    })
  }

  function remove(id: number) {
    update(list => list.filter(o => o.id !== id).map((o, idx) => ({ ...o, order: idx + 1 })))
    if (editingId === id)
      setEditingId(null)
  }

  // New offers land at the TOP (order 1) with their editor open — the form
  // renders inline under the selected row.
  function addOffer() {
    const fresh = blankOffer()
    update(list => [fresh, ...list].map((o, idx) => ({ ...o, order: idx + 1 })))
    setEditingId(fresh.id)
  }

  async function saveOffers() {
    if (!container)
      return
    setBusy(true)
    setMsg(null)
    try {
      await updateContainerOffers(container.id, toPayload(container.offers))
      // Re-fetch so new offers pick up their real DB ids.
      const rows = await fetchSportOfferContainers(sportId)
      const keep = container.id
      seed(rows)
      setContainerId(keep)
      setEditingId(null)
      setMsg({ kind: 'ok', text: 'offers saved' })
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  async function addContainer() {
    const name = (newName ?? '').trim()
    if (!name)
      return
    setBusy(true)
    setMsg(null)
    try {
      const created = await createOfferContainer(sportId, name)
      const rows = await fetchSportOfferContainers(sportId)
      seed(rows)
      setContainerId(created.id)
      setNewName(null)
      setEditingId(null)
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  async function renameContainer() {
    const name = (renaming ?? '').trim()
    if (!name || !container)
      return
    setBusy(true)
    setMsg(null)
    try {
      await renameOfferContainer(container.id, name)
      const id = container.id
      setContainerList(prev => (prev ?? []).map(c => (c.id === id ? { ...c, name } : c)))
      setRenaming(null)
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  async function removeContainer() {
    if (!container)
      return
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete container "${container.name}" (${container.offers.length} offers)? This is permanent.`))
      return
    setBusy(true)
    setMsg(null)
    try {
      await deleteOfferContainer(container.id)
      const rows = await fetchSportOfferContainers(sportId)
      seed(rows)
      setContainerId(null)
      setEditingId(null)
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  // Brand resolution for rendering: catalog wins, import fallback otherwise.
  function brandOf(o: EditorOffer): { hex: string, logoSrc: string, label: string } {
    const book = o.sportsbook_id != null ? books.find(b => b.id === o.sportsbook_id) : undefined
    return {
      hex: book?.brand_color ?? o.brand_hex ?? '#0DB5FF',
      logoSrc: book?.logo_url ? assetUrl(book.logo_url) : o.logo,
      label: book?.name ?? o.company,
    }
  }

  // Resolved full regulatory statement: the offer's override wins, else the
  // book-level default from the catalog. '' means no statement — no caret.
  function fullRegulatoryOf(o: EditorOffer): string {
    if (o.regulatory_full.trim())
      return o.regulatory_full
    const book = o.sportsbook_id != null ? books.find(b => b.id === o.sportsbook_id) : undefined
    return book?.regulatory_full ?? ''
  }

  function toggleRegulatory(o: EditorOffer, companyLabel: string) {
    const opening = !expandedIds.has(o.id)
    if (opening) {
      // Part of the offers analytics contract (alongside views + CTA clicks):
      // expansion is a tracked event so there's evidence the statement is
      // reachable. Pushes to GTM's dataLayer when the host page has one —
      // no-op in the bare portal, live once the offers-sdk embed exists.
      const dl = (window as unknown as { dataLayer?: Record<string, unknown>[] }).dataLayer
      if (Array.isArray(dl)) {
        dl.push({
          event: 'offer_terms_expanded',
          offer_company: companyLabel,
          offer_name: o.headline,
        })
      }
    }
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(o.id))
        next.delete(o.id)
      else
        next.add(o.id)
      return next
    })
  }

  // Short terms line + expand caret + (when open) the full statement.
  // Shared by every card variant — grid, compact sidebar row, featured.
  function renderTerms(o: EditorOffer, companyLabel: string) {
    const full = fullRegulatoryOf(o)
    const hasFull = full.trim() !== ''
    if (!o.terms && !hasFull)
      return null
    const open = hasFull && expandedIds.has(o.id)
    return (
      <>
        <div className="of-terms">
          {o.terms}
          {hasFull && (
            <button
              type="button"
              className="of-reg-caret"
              aria-expanded={open}
              title={open ? 'Hide full regulatory statement' : 'Show full regulatory statement'}
              onClick={() => toggleRegulatory(o, companyLabel)}
            >
              {open ? '▲' : '▼'}
            </button>
          )}
        </div>
        {open && <div className="of-reg-full">{full}</div>}
      </>
    )
  }

  function renderForm(o: EditorOffer) {
    const usSelection = o.regions.filter(r => US_CODES.has(r.code)).map(r => r.code).sort()
    const intlNames = o.regions.filter(r => !US_CODES.has(r.code)).map(r => r.name || r.code).sort()
    const book = o.sportsbook_id != null ? books.find(b => b.id === o.sportsbook_id) : undefined
    const inheritedReg = book?.regulatory_full ?? ''
    return (
      <div className="ofx-form">
        <div className="ofx-form-head">
          <span className="b-mono">
            EDIT OFFER
            {' '}
            <span className="b-mono-dim">
              #
              {o.order}
            </span>
          </span>
          <button type="button" className="of-icon-btn" onClick={() => setEditingId(null)}>CLOSE</button>
        </div>
        <label className="b-mono">
          SPORTSBOOK
          <select
            className="b-input"
            value={o.sportsbook_id ?? ''}
            onChange={e => setBook(o.id, Number(e.target.value))}
          >
            <option value="" disabled>
              {o.company ? `— keep "${o.company}" (not in catalog) —` : 'choose a sportsbook…'}
            </option>
            {availableBooks.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </label>
        {EDITABLE.map(f => (
          <label key={f.key} className="b-mono">
            {f.label}
            <input
              className="b-input"
              value={o[f.key]}
              onChange={e => setField(o.id, f.key, e.target.value)}
            />
          </label>
        ))}
        <label className="b-mono">
          REGULATORY — FULL STATEMENT
          <textarea
            className="b-input ofx-reg-input"
            rows={4}
            value={o.regulatory_full}
            onChange={e => setField(o.id, 'regulatory_full', e.target.value)}
            placeholder={inheritedReg
              ? `blank = inherit ${book!.name}'s statement`
              : 'plain text — line breaks preserved'}
            spellCheck={false}
          />
          {o.regulatory_full.trim() === '' && (
            inheritedReg.trim()
              ? (
                  <span className="ofx-reg-inherit b-mono-dim">
                    inheriting from
                    {' '}
                    {book!.name}
                    :
                    <span className="ofx-reg-inherit-text">{inheritedReg}</span>
                  </span>
                )
              : (
                  <span className="ofx-reg-inherit b-mono-dim">
                    {book
                      ? `no default on ${book.name} (set one on its operator page) — the card shows no caret`
                      : 'no book selected, no default to inherit — the card shows no caret'}
                  </span>
                )
          )}
        </label>
        <label className="b-mono">
          STATUS
          <select
            className="b-input"
            value={o.status}
            onChange={e => setField(o.id, 'status', e.target.value)}
          >
            <option value="published">published</option>
            <option value="draft">draft</option>
          </select>
        </label>
        <label className="b-mono">
          EXPIRES
          <input
            type="date"
            className="b-input"
            value={o.expires_at ? o.expires_at.slice(0, 10) : ''}
            onChange={e => setField(o.id, 'expires_at', e.target.value ? `${e.target.value}T00:00:00Z` : null)}
          />
        </label>
        <div className="ofx-regions">
          <span className="b-mono ofx-regions-label">REGIONS — US STATES</span>
          <StateGrid selected={usSelection} onChange={codes => setUsRegions(o.id, codes)} />
          <StringList
            label="INTERNATIONAL"
            values={intlNames}
            onChange={names => setIntlRegions(o.id, names)}
            placeholder="e.g. Ontario, United Kingdom (comma-separated ok)"
          />
        </div>
        <div className="ofx-form-foot">
          <button
            type="button"
            className="of-icon-btn of-delete-btn"
            onClick={() => remove(o.id)}
          >
            ✕ DELETE OFFER
          </button>
        </div>
        <div className="of-edit-hint b-mono-dim">
          Picking a sportsbook stamps the tracking identity, brand color, and
          logo from the catalog. Ordering lives in the preview — drag the
          cards. Changes persist when you hit SAVE OFFERS.
        </div>
      </div>
    )
  }

  function renderCard(o: EditorOffer, idx: number) {
    const brand = brandOf(o)
    const darkCta = brand.label === 'Underdog'
    const isDraft = o.status !== 'published'
    const sidebar = layout === 'sidebar'
    const featured = sidebar && idx === 0
    const dragProps = {
      'draggable': true,
      'onDragStart': (e: React.DragEvent) => {
        e.dataTransfer.effectAllowed = 'move'
        setDragId(o.id)
      },
      'onDragEnter': () => reorderTo(o.id),
      'onDragOver': (e: React.DragEvent) => e.preventDefault(),
      'onDragEnd': () => setDragId(null),
      'onDrop': (e: React.DragEvent) => e.preventDefault(),
      'title': 'Drag to reorder',
      'aria-grabbed': dragId === o.id,
    }
    const stateClasses
      = `${isDraft ? ' is-draft' : ''}${dragId === o.id ? ' is-dragging' : ''}${editingId === o.id ? ' is-selected' : ''}`
    const orderChip = <span className="of-order-chip" aria-hidden="true">{`#${o.order}`}</span>
    const logoTile = (
      <div
        className="of-logo"
        style={{ background: brand.hex }}
      >
        {brand.logoSrc
          ? <img src={brand.logoSrc} alt={brand.label} loading="lazy" />
          : <span className="of-logo-text">{brand.label.slice(0, 2).toUpperCase() || '?'}</span>}
      </div>
    )

    // Sidebar = same card chrome as the grid, compact two-column row —
    // except the FIRST card, which is featured (stacked, brand border).
    // Drag a card into slot 1 to audition the highlight.
    if (sidebar && !featured) {
      return (
        <div key={o.id} className={`of-card of-card--compact${stateClasses}`} {...dragProps}>
          {isDraft && <span className="of-draft-tag">DRAFT</span>}
          {orderChip}
          <div className="of-compact-row">
            <div className="of-compact-left">
              {logoTile}
              <div className="of-copy">
                <div className="of-company">{brand.label}</div>
                <div className="of-headline">{o.headline || '(untitled offer)'}</div>
              </div>
            </div>
            <a
              className="of-cta of-cta--compact"
              href={o.cta_url}
              target="_blank"
              rel="noopener noreferrer"
              draggable={false}
              style={{ background: brand.hex, color: darkCta ? '#000' : '#fff' }}
            >
              {o.cta_text || 'Get Bonus'}
            </a>
          </div>
          {renderTerms(o, brand.label)}
        </div>
      )
    }

    return (
      <div
        key={o.id}
        className={`of-card${featured ? ' of-card--featured' : ''}${stateClasses}`}
        style={featured ? { borderColor: brand.hex } : undefined}
        {...dragProps}
      >
        {isDraft && <span className="of-draft-tag">DRAFT</span>}
        {orderChip}
        <div className="of-card-body">
          {logoTile}
          <div className="of-copy">
            <div className="of-company">{brand.label}</div>
            <div className="of-headline">{o.headline || '(untitled offer)'}</div>
          </div>
        </div>
        <a
          className="of-cta"
          href={o.cta_url}
          target="_blank"
          rel="noopener noreferrer"
          draggable={false}
          style={{ background: brand.hex, color: darkCta ? '#000' : '#fff' }}
        >
          {o.cta_text || 'Get Bonus'}
        </a>
        {renderTerms(o, brand.label)}
      </div>
    )
  }

  return (
    <div className="ad-editor-section of-poc">
      <style>{OFFERS_CSS}</style>
      <h3 className="b-mono">OFFERS</h3>
      <p className="b-mono-dim ad-section-hint">
        Containers and offers persist to Supabase. Container create/rename/
        delete save immediately; offer edits and ordering save per container
        via SAVE OFFERS. Edit on the left, preview on the right; drag preview
        cards to reorder.
      </p>
      {msg && (
        <p className={`b-mono afx-msg${msg.kind === 'err' ? ' is-err' : ''}`}>
          {msg.kind === 'err' ? '⚠ ' : '✓ '}
          {msg.text}
        </p>
      )}

      {containerList === null
        ? <p className="b-mono-dim">LOADING…</p>
        : (
            <div className="ofx-split">
              {/* containers, offer list, edit form — rendered on the RIGHT
                  (CSS order: 2) */}
              <div className="ofx-left">
                <div className="of-poc-toolbar">
                  <div className="b-seg">
                    {containerList.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        className={`b-seg-btn${container && c.id === container.id ? ' is-active' : ''}`}
                        onClick={() => { setContainerId(c.id); setEditingId(null); setRenaming(null) }}
                      >
                        {c.name.replace(/ Offer Widget$/i, '')}
                        {baseline[c.id] !== undefined
                          && JSON.stringify(toPayload(c.offers)) !== baseline[c.id]
                          && <span className="ofx-dirty-dot" title="unsaved changes">●</span>}
                      </button>
                    ))}
                  </div>
                  {newName === null
                    ? (
                        <button type="button" className="of-icon-btn" disabled={busy} onClick={() => { setNewName(''); setRenaming(null) }}>
                          + NEW CONTAINER
                        </button>
                      )
                    : (
                        <span className="of-new-container">
                          <input
                            className="b-input"
                            value={newName}
                            autoFocus
                            placeholder="container name…"
                            onChange={e => setNewName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter')
                                addContainer()
                              if (e.key === 'Escape')
                                setNewName(null)
                            }}
                          />
                          <button type="button" className="of-icon-btn" disabled={busy} onClick={addContainer}>ADD</button>
                          <button type="button" className="of-icon-btn" onClick={() => setNewName(null)}>CANCEL</button>
                        </span>
                      )}
                  {container && renaming === null && (
                    <button type="button" className="of-icon-btn" disabled={busy} onClick={() => { setRenaming(container.name); setNewName(null) }}>
                      RENAME
                    </button>
                  )}
                  {container && renaming !== null && (
                    <span className="of-new-container">
                      <input
                        className="b-input"
                        value={renaming}
                        autoFocus
                        onChange={e => setRenaming(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter')
                            renameContainer()
                          if (e.key === 'Escape')
                            setRenaming(null)
                        }}
                      />
                      <button type="button" className="of-icon-btn" disabled={busy} onClick={renameContainer}>SAVE</button>
                      <button type="button" className="of-icon-btn" onClick={() => setRenaming(null)}>CANCEL</button>
                    </span>
                  )}
                  {container && (
                    <button type="button" className="of-icon-btn of-delete-btn" disabled={busy} onClick={removeContainer}>
                      ✕ DELETE CONTAINER
                    </button>
                  )}
                </div>

                {container
                  ? (
                      <>
                        <div className="of-poc-toolbar">
                          <button type="button" className="b-btn-primary of-new-btn b-mono" onClick={addOffer}>
                            + NEW OFFER
                          </button>
                          <button
                            type="button"
                            className={`b-btn-primary of-new-btn b-mono${dirty ? '' : ' ofx-save-idle'}`}
                            disabled={busy || !dirty}
                            onClick={saveOffers}
                          >
                            {busy ? 'SAVING…' : dirty ? 'SAVE OFFERS ●' : 'SAVED'}
                          </button>
                          <span className="b-mono-dim of-poc-count">
                            {offers.length}
                            {' '}
                            offers
                          </span>
                        </div>

                        <div className="ofx-list">
                          {offers.map((o) => {
                            const brand = brandOf(o)
                            return (
                              <div key={o.id} className="ofx-list-item">
                                <button
                                  type="button"
                                  className={`ofx-list-row${editingId === o.id ? ' is-active' : ''}${o.status !== 'published' ? ' is-draft' : ''}`}
                                  onClick={() => setEditingId(editingId === o.id ? null : o.id)}
                                >
                                  <span className="ofx-row-order b-mono-dim">{o.order}</span>
                                  <span className="ofx-row-logo" style={{ background: brand.hex }}>
                                    {brand.logoSrc
                                      ? <img src={brand.logoSrc} alt="" loading="lazy" />
                                      : <span>{brand.label.slice(0, 2).toUpperCase() || '?'}</span>}
                                  </span>
                                  <span className="ofx-row-copy">
                                    <span className="ofx-row-company">{brand.label || '(no sportsbook)'}</span>
                                    <span className="ofx-row-headline">{o.headline || '(untitled offer)'}</span>
                                  </span>
                                  {o.status !== 'published' && <span className="ofx-row-tag">DRAFT</span>}
                                </button>
                                {editingId === o.id && renderForm(o)}
                              </div>
                            )
                          })}
                          {offers.length === 0 && (
                            <p className="b-mono-dim ad-list-empty">no offers in this container yet</p>
                          )}
                        </div>
                      </>
                    )
                  : (
                      <p className="b-mono-dim ad-list-empty">
                        no offer containers for this sport — create one above
                      </p>
                    )}
              </div>

              {/* live preview (render + drag-reorder only) — rendered on the
                  LEFT (CSS order: 1) */}
              {container && (
                <div className="ofx-right">
                  <div className="of-poc-toolbar">
                    <span className="b-mono-dim ofx-preview-label">LIVE PREVIEW</span>
                    <div className="b-seg">
                      {(['three', 'two', 'one', 'sidebar'] as Layout[]).map(l => (
                        <button
                          key={l}
                          type="button"
                          className={`b-seg-btn${l === layout ? ' is-active' : ''}`}
                          onClick={() => setLayout(l)}
                        >
                          {l.toUpperCase()}
                        </button>
                      ))}
                    </div>
                    <span className="b-mono-dim of-poc-count">drag to reorder</span>
                  </div>
                  <div className={`of-grid of-grid--${layout}`}>
                    {offers.map((o, idx) => renderCard(o, idx))}
                    {offers.length === 0 && (
                      <p className="b-mono-dim ad-list-empty">nothing to preview</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
    </div>
  )
}

// Card look ported from the handoff's offers.css (--of-* tokens, 3px-border
// grid card, compact sidebar treatment, brand-hex CTA that never themes),
// plus the split-layout chrome (ofx-*). Dark keys off .brutalist-root.is-dark.
const OFFERS_CSS = `
.of-poc {
  --of-card-bg: #F3F9FF;
  --of-card-border: #ACD2FF;
  --of-ink: #151617;
  --of-ink-muted: #4a4d52;
}
.brutalist-root.is-dark .of-poc {
  --of-card-bg: #0F1726;
  --of-card-border: #1F3656;
  --of-ink: #E8EEF7;
  --of-ink-muted: #C3CEDD;
}
.afx-msg { font-size: 12px; margin: 6px 0 0; }
.afx-msg.is-err { color: #b91c1c; }
.brutalist-root.is-dark .afx-msg.is-err { color: #f87171; }
.ofx-split {
  display: grid;
  /* Live preview on the LEFT holding a fixed third (the sidebar layout, the
     default, is designed for exactly that width); the container/form side
     gets the room on the RIGHT. The order property flips the two panels
     visually while the DOM keeps form-then-preview source order. */
  grid-template-columns: minmax(280px, 1fr) minmax(0, 2fr);
  gap: 20px;
  align-items: start;
}
@media (max-width: 1100px) {
  .ofx-split { grid-template-columns: 1fr; }
}
.ofx-left, .ofx-right { min-width: 0; }
.ofx-left { order: 2; }
.ofx-right { order: 1; }
/* Divider now sits on the left edge of the container/form panel (right side). */
.ofx-left {
  border-left: 1px solid var(--of-card-border);
  padding-left: 20px;
}
@media (max-width: 1100px) {
  /* Stacked: preview first, then containers; no vertical divider. */
  .ofx-left { border-left: none; padding-left: 0; order: 2; }
  .ofx-right { order: 1; }
}
.ofx-preview-label { font-size: 11px; letter-spacing: 0.08em; }
.ofx-dirty-dot { margin-left: 5px; color: #b45309; }
.ofx-save-idle { opacity: 0.5; }
.ofx-list { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; }
.ofx-list-item { display: flex; flex-direction: column; gap: 4px; }
.ofx-list-item .ofx-list-row { width: 100%; }
.ofx-regions { display: flex; flex-direction: column; gap: 4px; font-size: 11px; }
.ofx-regions-label { font-size: 11px; }
.ofx-list-row {
  display: grid;
  grid-template-columns: 20px 24px 1fr auto;
  gap: 8px;
  align-items: center;
  padding: 5px 8px;
  border: 1px solid var(--of-card-border);
  border-radius: 5px;
  background: none;
  color: var(--of-ink);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}
.ofx-list-row.is-active { border-color: currentColor; box-shadow: 0 0 0 1px currentColor; }
.ofx-list-row.is-draft { opacity: 0.55; }
.ofx-row-order { font-size: 10px; text-align: right; }
.ofx-row-logo {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
}
.ofx-row-logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
.ofx-row-copy { display: flex; flex-direction: column; min-width: 0; }
.ofx-row-company { font-size: 10px; color: var(--of-ink-muted); line-height: 13px; }
.ofx-row-headline {
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ofx-row-tag {
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  background: #7f1d1d;
  color: #fff;
  border-radius: 3px;
}
.ofx-form {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid var(--of-card-border);
  border-left: 3px solid var(--of-card-border);
  border-radius: 6px;
  padding: 10px;
  margin: 0 0 4px 20px;
}
.ofx-form label { display: flex; flex-direction: column; gap: 2px; font-size: 11px; }
.ofx-form-head { display: flex; justify-content: space-between; align-items: center; }
.ofx-form-foot { display: flex; justify-content: flex-end; }
.of-poc-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin: 8px 0 12px;
}
.of-new-btn { padding: 4px 12px; font-size: 12px; cursor: pointer; }
.of-new-btn:disabled { cursor: default; }
.of-new-container { display: inline-flex; gap: 6px; align-items: center; }
.of-new-container .b-input { font-size: 12px; padding: 3px 8px; width: 200px; }
.of-poc-count { font-size: 12px; }
.of-grid { display: grid; gap: 8px; align-items: stretch; }
.of-grid--three { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.of-grid--two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.of-grid--one { grid-template-columns: 1fr; }
.of-grid--sidebar { grid-template-columns: 1fr; width: 100%; gap: 5px; }
@media (max-width: 900px) {
  .of-grid--three, .of-grid--two { grid-template-columns: 1fr; }
}
.of-card {
  position: relative;
  padding: 10px;
  border-radius: 8px;
  border: 3px solid var(--of-card-border);
  background: var(--of-card-bg);
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: Poppins, system-ui, sans-serif;
  cursor: grab;
}
.of-card.is-dragging { opacity: 0.4; cursor: grabbing; }
.of-card.is-selected { outline: 2px dashed currentColor; outline-offset: 2px; }
.of-order-chip {
  position: absolute;
  top: -10px;
  left: -10px;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  border-radius: 10px;
  background: var(--of-card-border);
  color: var(--of-ink);
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}
.of-compact-row {
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 12px;
  align-items: center;
}
.of-compact-left {
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 10px;
  align-items: center;
  min-width: 0;
}
.of-card--compact .of-logo { width: 56px; height: 56px; flex-basis: 56px; }
.of-card--compact .of-company { font-size: 10px; line-height: 14px; }
.of-card--compact .of-headline { font-size: 12px; line-height: 16px; }
.of-cta--compact {
  height: 48px;
  padding: 8px 10px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
}
.of-card--featured .of-company { font-size: 14px; line-height: 18px; }
.of-card--featured .of-headline { font-size: 16px; line-height: 22px; }
.of-card--featured .of-cta { font-size: 16px; }
.is-draft { opacity: 0.55; }
.of-draft-tag {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  background: #7f1d1d;
  color: #fff;
  border-radius: 3px;
  z-index: 1;
}
.of-card-body {
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 12px;
  align-items: center;
}
.of-logo {
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: #fff;
}
.of-logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
.of-logo-text { color: #fff; font-weight: 700; font-size: 18px; }
.of-company { color: var(--of-ink-muted); font-size: 13px; line-height: 18px; }
.of-headline { color: var(--of-ink); font-size: 16px; font-weight: 600; line-height: 21px; }
.of-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
}
.of-terms {
  color: var(--of-ink-muted);
  font-size: 10px;
  line-height: 14px;
  text-align: center;
}
.of-reg-caret {
  background: none;
  border: none;
  padding: 0 3px;
  margin-left: 4px;
  color: inherit;
  font-size: 8px;
  line-height: 1;
  vertical-align: 1px;
  cursor: pointer;
}
.of-reg-full {
  color: var(--of-ink-muted);
  font-size: 10px;
  line-height: 14px;
  text-align: left;
  white-space: pre-line;
  border-top: 1px solid var(--of-card-border);
  padding-top: 6px;
}
.ofx-reg-input {
  font-family: inherit;
  font-size: 12px;
  resize: vertical;
  min-height: 60px;
}
.ofx-reg-inherit { display: block; font-size: 10px; line-height: 14px; margin-top: 2px; }
.ofx-reg-inherit-text { display: block; white-space: pre-line; opacity: 0.85; }
.of-icon-btn {
  font-size: 10px;
  padding: 2px 6px;
  cursor: pointer;
  background: none;
  border: 1px solid currentColor;
  color: inherit;
  border-radius: 3px;
  line-height: 1.2;
}
.of-icon-btn:disabled { opacity: 0.3; cursor: default; }
.of-delete-btn { margin-left: auto; color: #b91c1c; }
.brutalist-root.is-dark .of-poc .of-delete-btn { color: #f87171; }
.of-edit-hint { font-size: 11px; }
`
